import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAssessment, submitAssessment, resumeFromToken, patchAnswers, recordLandingView } from './api';
import { applyBranding } from './theme';
import { injectGtm } from './lib/gtm';
import { setCanonical, setMetaDescription, setOgImage, setJsonLd } from './lib/headTags';
import { buildEventJsonLd } from './lib/eventSchema';
import { BrandProvider } from './components/BrandContext';
import { PhaseProvider } from './components/PhaseContext';
import { getAttributionPayload } from './attribution';
import type { Assessment, Answers, Lead, SubmissionScores, PageStyle, RegistrationStatus } from './types';
import Intro from './screens/Intro';
import EmailGate from './screens/EmailGate';
import { offersNewsletterOnEvent } from './lib/newsletterOptIn';
import GatePage from './screens/GatePage';
import Questions from './screens/Questions';
import Result from './screens/Result';
import { Block } from './components/Blocks';
import BrokenPage from './components/BrokenPage';
import { interpolateBlocks } from './lib/interpolate';
import { stepTwoApplies } from './lib/stepTwo';
import { upgradeStepApplies, readDeclined, writeDeclined } from './lib/upgradeStep';
import StepTwoFrame from './screens/StepTwoFrame';
import UpgradeStep from './screens/UpgradeStep';
import type { ContentBlock } from './types';

const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '';

type Phase = 'loading' | 'error' | 'intro' | 'email-gate' | 'upgrade' | 'gate' | 'questions'
  | 'submitting' | 'result'
  | 'resuming'           // ?resume=<token> is in URL — verifying before deciding next phase
  | 'external-redirect'; // hostingMode='external' and no resume token + no saved session

/** Preview-mode selection passed via ?preview=<phase> URL param. Lets the admin audit
 *  every page without completing a real submission flow. Populates mock lead/answers
 *  so downstream screens render with content. */
interface PreviewState {
  active: boolean;
  phase?: Phase;
  questionIdx?: number;
  tierKey?: string;
}

interface PriorResult {
  found: boolean;
  primaryGap?: string[];
}

interface State {
  phase: Phase;
  assessment?: Assessment;
  error?: string;
  lead?: Lead;
  answers: Answers;
  scores?: SubmissionScores;
  submissionId?: string;
  priorResult?: PriorResult;
  /** When set, this session was resumed from a HubSpot widget registration.
   *  The final submit branches to PATCH answers on the existing submission
   *  rather than POST-ing a new one. */
  resumedSubmissionId?: string;
  /** Registration status at resume time — drives gate-page copy branching. */
  registrationStatus?: RegistrationStatus | null;
  /** Auto-VIP via TSB Membership, known at resume time (submissions.tier_key
   *  = 'vip') — see ResumeResponse.memberVip's own doc comment for what this
   *  does and doesn't cover. Drives whether GatePage shows an upgrade offer. */
  memberVip?: boolean;
  /** The resumed registration's tier_key / tier_source (resume response).
   *  Skips the upgrade step for anyone on a paid tier and drives the
   *  thank-you's "pass confirmed" line. See lib/upgradeStep.ts. */
  tierKey?: string | null;
  tierSource?: string | null;
  preview: PreviewState;
}

type Action =
  | { type: 'loaded'; assessment: Assessment }
  | { type: 'load-failed'; error: string }
  | { type: 'start' }
  | { type: 'lead-captured'; lead: Lead }
  | { type: 'gate-continue'; priorResult?: PriorResult }
  | { type: 'answers-complete'; answers: Answers }
  | { type: 'submitted'; scores: SubmissionScores; submissionId: string }
  | { type: 'submit-failed'; error: string }
  | { type: 'restart' }
  | { type: 'resume-from-embed'; lead: Lead; submissionId: string; status: RegistrationStatus | null; memberVip?: boolean;
      tierKey?: string | null; tierSource?: string | null; upgradeDeclined?: boolean }
  | { type: 'decline-upgrade' }
  | { type: 'survey-opted-out'; status: RegistrationStatus }
  | { type: 'preview-jump'; phase: Phase; questionIdx?: number; tierKey?: string }
  | { type: 'set-registration-status'; status: import('./types').RegistrationStatus | null };

// ---------- session persistence + initial-route resolver ----------
//
// The taker can be interrupted mid-flow (browser refresh, tab close + reopen). Without
// persistence, the user lands back on intro + loses progress. sessionStorage keyed by
// slug persists { phase, lead, submissionId, priorResult } for a short window.

const SESSION_KEY_PREFIX = 'assessment-flow:';
const SESSION_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

interface PersistedSession {
  phase: Phase;
  lead?: Lead;
  answers?: Answers;
  resumedSubmissionId?: string;
  registrationStatus?: RegistrationStatus | null;
  priorResult?: PriorResult;
  tierKey?: string | null;
  tierSource?: string | null;
  savedAt: number;
}

function loadSession(slug: string): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY_PREFIX + slug);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY_PREFIX + slug);
      return null;
    }
    return parsed;
  } catch { return null; }
}

function saveSession(slug: string, s: State): void {
  // Only persist meaningful in-flight phases. Don't persist intro/loading/result.
  const persistablePhases: Phase[] = ['email-gate', 'upgrade', 'gate', 'questions', 'submitting'];
  if (!persistablePhases.includes(s.phase)) return;
  try {
    const payload: PersistedSession = {
      phase: s.phase,
      lead: s.lead,
      answers: s.answers,
      resumedSubmissionId: s.resumedSubmissionId,
      registrationStatus: s.registrationStatus,
      priorResult: s.priorResult,
      tierKey: s.tierKey,
      tierSource: s.tierSource,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(SESSION_KEY_PREFIX + slug, JSON.stringify(payload));
  } catch { /* quota or disabled */ }
}

function clearSession(slug: string): void {
  try { sessionStorage.removeItem(SESSION_KEY_PREFIX + slug); } catch { /* */ }
}

/** Parse preview query params once. Returns null when preview mode isn't requested. */
function readPreviewFromUrl(): PreviewState {
  try {
    const p = new URLSearchParams(window.location.search);
    const phaseRaw = p.get('preview');
    if (!phaseRaw) return { active: false };
    const validPhases: Phase[] = ['intro', 'email-gate', 'upgrade', 'gate', 'questions', 'submitting', 'result', 'external-redirect', 'error'];
    const phase = validPhases.includes(phaseRaw as Phase) ? (phaseRaw as Phase) : 'intro';
    const qIdx = Number(p.get('q') ?? '0');
    const questionIdx = Number.isFinite(qIdx) && qIdx >= 0 ? qIdx : 0;
    const tierKey = p.get('tier') ?? undefined;
    return { active: true, phase, questionIdx, tierKey };
  } catch { return { active: false }; }
}

/** Determine the initial phase after the assessment loads. Priority:
 *    1. Preview mode (?preview=): jump to requested phase with mock data
 *    2. Resume token in URL: start at 'resuming' (the useEffect verifies it)
 *    3. Stored session: restore whatever phase we left off at
 *    4. hostingMode='external' + no resume + no session: 'external-redirect'
 *    5. Default: 'intro' (engine-hosted flow) */
function resolveInitialPhase(assessment: Assessment, preview: PreviewState, slug: string): {
  phase: Phase;
  lead?: Lead;
  answers?: Answers;
  resumedSubmissionId?: string;
  registrationStatus?: RegistrationStatus | null;
  priorResult?: PriorResult;
  tierKey?: string | null;
  tierSource?: string | null;
} {
  if (preview.active) return { phase: preview.phase ?? 'intro' };
  const hasResumeToken = new URLSearchParams(window.location.search).get('resume');
  if (hasResumeToken) return { phase: 'resuming' };
  const saved = loadSession(slug);
  if (saved) {
    return {
      phase: saved.phase,
      lead: saved.lead,
      answers: saved.answers,
      resumedSubmissionId: saved.resumedSubmissionId,
      registrationStatus: saved.registrationStatus,
      priorResult: saved.priorResult,
      tierKey: saved.tierKey,
      tierSource: saved.tierSource,
    };
  }
  if ((assessment.config as any).hostingMode === 'external') {
    return { phase: 'external-redirect' };
  }
  return { phase: 'intro' };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'loaded': {
      // Preview mode: stash the assessment but STAY at phase='loading' so the
      // preview-jump useEffect fires with mock lead/scores populated. Without this,
      // the gate/questions/result phases would render with no lead → blank screen.
      if (state.preview.active) {
        return { ...state, assessment: action.assessment };
      }
      const slug = action.assessment.slug;
      const resolved = resolveInitialPhase(action.assessment, state.preview, slug);
      return {
        ...state,
        assessment: action.assessment,
        phase: resolved.phase,
        lead: resolved.lead ?? state.lead,
        answers: resolved.answers ?? state.answers,
        resumedSubmissionId: resolved.resumedSubmissionId ?? state.resumedSubmissionId,
        registrationStatus: resolved.registrationStatus ?? state.registrationStatus,
        priorResult: resolved.priorResult ?? state.priorResult,
        tierKey: resolved.tierKey ?? state.tierKey,
        tierSource: resolved.tierSource ?? state.tierSource,
      };
    }
    case 'load-failed':
      return { ...state, phase: 'error', error: action.error };
    case 'start': {
      const gate = state.assessment?.config.emailGate;
      if (gate?.enabled && (gate.position ?? 'before_questions') === 'before_questions') {
        return { ...state, phase: 'email-gate' };
      }
      return { ...state, phase: 'questions' };
    }
    case 'lead-captured': {
      // If gate page is configured, go there instead of straight to questions
      const hasGate = !!state.assessment?.config.gatePage;
      return { ...state, lead: action.lead, phase: hasGate ? 'gate' : 'questions' };
    }
    case 'gate-continue':
      return { ...state, phase: 'questions', priorResult: action.priorResult };
    case 'answers-complete': {
      const gate = state.assessment?.config.emailGate;
      const needsGate = gate?.enabled && gate.position === 'after_questions' && !state.lead;
      return {
        ...state,
        answers: action.answers,
        phase: needsGate ? 'email-gate' : 'submitting',
      };
    }
    case 'submitted':
      return { ...state, phase: 'result', scores: action.scores, submissionId: action.submissionId };
    case 'submit-failed':
      return { ...state, phase: 'error', error: action.error };
    case 'restart':
      return {
        ...state, phase: 'intro',
        lead: undefined, answers: {}, scores: undefined, submissionId: undefined,
        priorResult: undefined, resumedSubmissionId: undefined, registrationStatus: undefined,
      };
    case 'resume-from-embed': {
      // User arrived via HubSpot widget redirect with a signed resume token.
      // Skip intro + email gate; land directly on the gate page with pre-filled lead.
      // With gatePage.stepTwo, a saved seat goes straight to the first question
      // ("Step 2 of 2") — see lib/stepTwo.ts for why. With gatePage.upgradeStep,
      // a free saved seat sees the upgrade offer first (lib/upgradeStep.ts).
      const gatePage = state.assessment?.config.gatePage;
      const straightIn = stepTwoApplies(gatePage, action.status);
      const upgrade = upgradeStepApplies({
        gatePage, status: action.status, tierKey: action.tierKey,
        memberVip: action.memberVip, declined: action.upgradeDeclined,
      });
      return {
        ...state,
        lead: action.lead,
        resumedSubmissionId: action.submissionId,
        registrationStatus: action.status,
        memberVip: action.memberVip ?? false,
        tierKey: action.tierKey ?? null,
        tierSource: action.tierSource ?? null,
        phase: upgrade ? 'upgrade' : gatePage && !straightIn ? 'gate' : 'questions',
      };
    }
    case 'decline-upgrade': {
      // "No thank you": no network call, no record — the free seat was saved
      // at registration. Lands where the flow would have gone without the step.
      const gatePage = state.assessment?.config.gatePage;
      const straightIn = stepTwoApplies(gatePage, state.registrationStatus, state.preview.active);
      return { ...state, phase: gatePage && !straightIn ? 'gate' : 'questions' };
    }
    case 'survey-opted-out':
      // Opting out from the step-two questions lands on the gate page's own
      // terminal "You're registered" state.
      return {
        ...state,
        registrationStatus: action.status,
        phase: state.phase === 'questions' && state.assessment?.config.gatePage ? 'gate' : state.phase,
      };
    case 'set-registration-status':
      return { ...state, registrationStatus: action.status };
    case 'preview-jump': {
      // Admin preview — jump to any phase with mocked lead/scores so downstream screens render.
      const mockLead: Lead = state.lead ?? { email: 'preview@example.com', firstName: 'Preview' };
      const assessment = state.assessment;
      const resultKeys = assessment ? Object.keys(assessment.config.results ?? {}) : [];
      const tierKey = action.tierKey && resultKeys.includes(action.tierKey)
        ? action.tierKey
        : resultKeys[0];
      const mockScores: SubmissionScores | undefined = tierKey
        ? ({ result: tierKey, total: 0, maxTotal: 0, sections: {} } as unknown as SubmissionScores)
        : state.scores;
      // ?preview=gate&tier=vip shows the buyer's "pass confirmed" line.
      const tierLines = assessment?.config.gatePage?.tierLines;
      const buyerTier = action.phase === 'gate' && action.tierKey && tierLines?.[action.tierKey] ? action.tierKey : null;
      return {
        ...state,
        phase: action.phase,
        lead: mockLead,
        scores: action.phase === 'result' ? mockScores : state.scores,
        tierKey: buyerTier,
        tierSource: buyerTier ? 'purchase' : null,
        preview: { ...state.preview, active: true, phase: action.phase, questionIdx: action.questionIdx, tierKey: action.tierKey },
      };
    }
    default:
      return state;
  }
}

export default function App() {
  const { slug = 'test' } = useParams();
  // Read preview query param BEFORE reducer init so the first render knows.
  const initialPreview = useMemo(() => readPreviewFromUrl(), []);
  const [state, dispatch] = useReducer(
    reducer,
    { phase: 'loading', answers: {}, preview: initialPreview },
  );
  const priorCheckDone = useRef(false);
  const resumeHandled = useRef(false);

  // Attribution hydration now runs once at the router root (see AttributionHydrator
  // in main.tsx) so every taker page captures UTMs, not just the two that render
  // <App>. Removed here 2026-09-18 to avoid two competing hydration call sites —
  // see main.tsx for the full rationale.

  // Engine-hosted landing-views beacon — fires once per mount so the funnel
  // can compute visitor → email-submitted conversion. Skipped in preview mode.
  useEffect(() => {
    if (!initialPreview.active && slug) recordLandingView(slug);
  }, [slug, initialPreview.active]);

  // Persist flow state on phase changes so refresh mid-flow restores instead of dumping
  // the user to intro. Skipped in preview mode.
  useEffect(() => {
    if (state.preview.active || !state.assessment) return;
    saveSession(slug, state);
  }, [state.phase, state.lead, state.answers, state.resumedSubmissionId, slug]);

  // Clear persisted session when flow completes or restarts.
  useEffect(() => {
    if (state.phase === 'result' || state.phase === 'intro' || state.phase === 'error') {
      clearSession(slug);
    }
  }, [state.phase, slug]);

  // Preview mode: after assessment loads, jump to the requested phase with mock data.
  useEffect(() => {
    if (!state.preview.active || !state.assessment || state.phase !== 'loading') return;
    dispatch({
      type: 'preview-jump',
      phase: state.preview.phase ?? 'intro',
      questionIdx: state.preview.questionIdx,
      tierKey: state.preview.tierKey,
    });
  }, [state.preview, state.assessment, state.phase]);

  // Fetch assessment on mount / slug change
  useEffect(() => {
    let alive = true;
    fetchAssessment(slug)
      .then((a) => { if (alive) dispatch({ type: 'loaded', assessment: a }); })
      .catch((e) => { if (alive) dispatch({ type: 'load-failed', error: e.message }); });
    return () => { alive = false; };
  }, [slug]);

  // Apply branding when assessment loads. Brand-system tokens (palette, fonts,
  // designTokens) come from the resolved brand bundle and override the legacy
  // config.branding fields in theme.ts.
  useEffect(() => {
    if (state.assessment) {
      const sa = state.assessment;
      applyBranding(sa.config.branding, { brand: sa.brand ?? null });
      document.title = sa.title;
      // Inject the brand-specific GTM container (or TSB fallback). Idempotent —
      // safe even if this effect runs twice in React StrictMode.
      injectGtm(sa.brand?.gtmContainerId ?? null);

      // ★ SEO/structured-data infra (2026-09-16) — previously NONE of this
      // existed anywhere in the taker app: hardcoded lang="en", no canonical,
      // no meta description, no og:image, no JSON-LD, on any event page ever.
      const ec = (sa.config as any).eventConfig ?? {};
      // Only override lang for a region this app actually serves distinctly
      // today (AU/NZ timezones) — everything else keeps the existing default
      // rather than guessing at a locale from a US/CA-shared 'en' page.
      const tz = typeof ec.eventTimezone === 'string' ? ec.eventTimezone : '';
      document.documentElement.lang = /^(Australia|Pacific)\//.test(tz) ? 'en-AU' : 'en';

      setCanonical();

      const heroSubhead = (() => {
        const first = (sa.config.intro?.blocks as Array<{ type?: string; subhead?: string }> | undefined)
          ?.find((b) => b?.type === 'eventHero');
        return typeof first?.subhead === 'string' ? first.subhead.replace(/<[^>]+>/g, '') : '';
      })();
      const description = typeof ec.metaDescription === 'string' && ec.metaDescription
        ? ec.metaDescription
        : heroSubhead;
      if (description) setMetaDescription(description);

      setOgImage(sa.brand?.logoUrl ?? sa.config.branding?.logoUrl ?? null);
      setJsonLd(buildEventJsonLd(sa));
    }
  }, [state.assessment]);

  // Resume from widget redirect — once assessment is loaded and phase='resuming',
  // verify the token. On success → gate/questions. On failure → external-redirect
  // (if hostingMode='external') or intro.
  // We KEEP the ?resume token in the URL so a refresh within the token's TTL re-verifies
  // and restores state. sessionStorage also persists the flow state as a second layer.
  useEffect(() => {
    if (state.preview.active) { resumeHandled.current = true; return; }
    if (!state.assessment || resumeHandled.current) return;
    if (state.phase !== 'resuming') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('resume');
    if (!token) { resumeHandled.current = true; return; }
    resumeHandled.current = true;
    let alive = true;
    resumeFromToken(token)
      .then((r) => {
        if (!alive) return;
        // If the event is in replay phase, skip the gate page entirely and send
        // the user straight to the replay page. The gate copy ("You're in. See
        // you May 12th") is stale for late registrants who just want the replay.
        const eventCfg = (state.assessment?.config as Record<string, unknown> | undefined)
          ?.eventConfig as Record<string, unknown> | undefined;
        if ((eventCfg?.phase as string | undefined) === 'replay' && state.assessment) {
          window.location.replace(`/replays/${encodeURIComponent(state.assessment.slug)}`);
          return;
        }
        let store: Storage | null = null;
        try { store = window.sessionStorage; } catch { /* blocked */ }
        dispatch({
          type: 'resume-from-embed',
          lead: { email: r.email, firstName: r.firstName ?? undefined },
          submissionId: r.submissionId,
          status: r.status,
          memberVip: r.memberVip,
          tierKey: r.tierKey ?? null,
          tierSource: r.tierSource ?? null,
          upgradeDeclined: readDeclined(store, r.submissionId),
        });
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('resume token failed:', e.message);
        // Fallback: restart resolves to intro or external-redirect as appropriate.
        // Fire a synthetic 'loaded' to re-run the resolver (without the resume token).
        params.delete('resume');
        const qs = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
        // Resetting resumeHandled so resolveInitialPhase runs again on the next 'loaded'.
        resumeHandled.current = false;
        if (state.assessment) dispatch({ type: 'loaded', assessment: state.assessment });
      });
    return () => { alive = false; };
  }, [state.assessment, state.phase, state.preview.active]);

  // Run prior-submission check when entering gate phase
  useEffect(() => {
    if (state.phase !== 'gate' || !state.assessment || !state.lead || priorCheckDone.current) return;
    const pc = state.assessment.config.gatePage?.priorCheck;
    if (!pc) return; // No prior check configured — just wait for user to click CTA
    priorCheckDone.current = true;

    const email = encodeURIComponent(state.lead.email);
    const assessmentSlug = encodeURIComponent(pc.assessmentSlug);
    fetch(`${API_BASE}/api/check-prior?email=${email}&assessmentSlug=${assessmentSlug}`)
      .then((r) => r.json())
      .then((data) => {
        // Store result — it'll be used when gate-continue fires
        (window as any).__priorResult = data as PriorResult;
      })
      .catch(() => {
        (window as any).__priorResult = { found: false };
      });
  }, [state.phase, state.assessment, state.lead]);

  // Handle submission. Branch:
  //   - resumedSubmissionId present → PATCH answers onto existing submission (widget-registered user)
  //   - otherwise → POST a new submission (fresh engine-only flow)
  // Preview mode short-circuits — admins jumping to ?preview=submitting
  // shouldn't write a real submission with mock data, and the screen would
  // otherwise flash through to result the moment the API responds.
  useEffect(() => {
    if (state.phase !== 'submitting' || !state.assessment || !state.lead) return;
    if (state.preview.active) return;
    let alive = true;
    const attribution = getAttributionPayload();
    const action = state.resumedSubmissionId
      ? patchAnswers(state.resumedSubmissionId, state.answers, attribution)
      : submitAssessment({
          assessmentSlug: state.assessment.slug,
          lead: state.lead,
          answers: state.answers,
          attribution,
        });
    action
      .then((r) => {
        if (!alive) return;
        dispatch({ type: 'submitted', scores: r.scores, submissionId: r.submissionId });
        // Push deterministic event_ids to GTM so the browser-side Meta Pixel tag can
        // carry the same event_id as the server-side CAPI send — required for dedup.
        // GTM reads ee_lead_event_id / ee_complete_reg_event_id via DLV variables.
        (window as unknown as { dataLayer?: unknown[] }).dataLayer?.push({
          event: 'ee_assessment_submitted',
          ee_lead_event_id: `lead:${r.submissionId}`,
          ee_complete_reg_event_id: `completeregistration:${r.submissionId}`,
        });
      })
      .catch((e) => { if (alive) dispatch({ type: 'submit-failed', error: e.message }); });
    return () => { alive = false; };
  }, [state.phase, state.assessment, state.lead, state.answers, state.resumedSubmissionId]);

  // Determine current page style
  const pageStyle = useMemo<PageStyle | undefined>(() => {
    const c = state.assessment?.config;
    if (!c) return undefined;
    if (state.phase === 'intro') return c.intro.pageStyle;
    if (state.phase === 'email-gate') return c.emailGate?.pageStyle;
    if (state.phase === 'gate' || state.phase === 'upgrade') return c.gatePage?.pageStyle;
    if (state.phase === 'result' && state.scores) return c.results[state.scores.result]?.pageStyle;
    return undefined;
  }, [state.assessment, state.phase, state.scores]);

  // Apply page-level overrides.
  // NOTE (Apr 20 2026): we intentionally ignore pageStyle.backgroundColor,
  // backgroundGradient, and textColor. Body bg + text are owned by the engine's
  // theme layer (light/dark) — phase-specific overrides used to clamp those to
  // a single color regardless of theme, which broke light/dark contrast on
  // intro/email-gate/gate/result phases. If an assessment genuinely needs a
  // phase-only bg that differs per theme, extend PageStyle with
  // `{ light?: {...}, dark?: {...} }` later. For now, only backgroundImageUrl
  // (non-gradient hero images) is still honored because it doesn't fight theme.
  useEffect(() => {
    const body = document.body;
    if (pageStyle?.backgroundImageUrl && !pageStyle.backgroundGradient) {
      body.style.backgroundImage = `url("${pageStyle.backgroundImageUrl}")`;
    }
    return () => {
      body.style.backgroundImage = '';
    };
  }, [pageStyle]);

  // Phase determines layout: intro + result + gate are full-width, others are centered card
  const isFullWidth = state.phase === 'intro' || state.phase === 'result' || state.phase === 'gate' || state.phase === 'upgrade';

  // Compute skip info from prior check
  const skipQuestionIds = useMemo(() => {
    const pc = state.assessment?.config.gatePage?.priorCheck;
    if (!pc || !state.priorResult?.found) return undefined;
    return pc.skipQuestionIds;
  }, [state.assessment, state.priorResult]);

  const transitionMessage = useMemo(() => {
    const pc = state.assessment?.config.gatePage?.priorCheck;
    if (!pc || !state.priorResult?.found) return undefined;
    // Replace {{primaryGap}} placeholder with actual value
    let msg = pc.transitionMessage;
    const gap = state.priorResult.primaryGap;
    if (gap && Array.isArray(gap) && gap.length > 0) {
      msg = msg.replace(/\{\{primaryGap\}\}/g, gap.join(', '));
    }
    return msg;
  }, [state.assessment, state.priorResult]);

  // Render
  if (state.phase === 'loading') return <div className="loading-screen"><div className="loading-spinner" /><span>Loading…</span></div>;
  if (state.phase === 'error') {
    // "Failed to fetch" / "Load failed" are the browser-default messages when
    // a fetch() can't reach the server (Wi-Fi blip, TLS reset, etc). The
    // BrokenPage variant reads the situation: a 404-style "not found" gets
    // the wrong-turn copy; everything else gets the generic error copy.
    const raw = state.error || '';
    const isNotFound = /not found|404/i.test(raw);
    const isNetworkError = /failed to fetch|load failed|network/i.test(raw);
    const message = isNetworkError
      ? "Looks like a brief connection hiccup. Your answers are still saved — please try again."
      : undefined;
    return <BrokenPage variant={isNotFound ? '404' : 'error'} message={message} errorDetail={raw || undefined} />;
  }
  if (!state.assessment) return null;

  const a = state.assessment;
  const branding = a.config.branding;
  const showBrandBar = a.config.intro.showLogo !== false;
  const programTitle = branding?.programTitle;
  // Brand system logo takes priority over the config-baked URL.
  // Falls back to config.branding.logoUrl for assessments not yet linked to a program.
  const effectiveLogoUrl = a.brand?.logoUrl ?? branding?.logoUrl ?? null;
  // Where the header logo should link to. Resolved server-side from
  // brand.logo_link_url (per-program override → brand-level default → null).
  // null = no anchor; the logo renders as a static image.
  const logoLinkUrl = a.brand?.logoLinkUrl ?? null;
  const showLogo = showBrandBar && !programTitle && !!effectiveLogoUrl;
  const showTitle = showBrandBar && !!programTitle;

  // Persistent funnel chrome — the siteNav/eventFooter blocks in intro.blocks,
  // found by TYPE rather than position, then rendered on every phase past the
  // intro (email-gate, gate, questions, result) so visitors keep brand context
  // and a real footer as they move through the flow.
  //
  // ★ REGRESSION (found 2026-09-16): this used to check ONLY
  // intro.blocks[0]?.type === 'siteNav', and only rendered the nav — never the
  // footer at all. Summit's intro.blocks opens with an announceBar, so siteNav
  // sits at index 1 — the index-0 check silently failed and Summit's
  // survey/gate/result phases had NO header for two months, and no phase past
  // the landing page has ever had a footer, on any event. Search by type, not
  // position, so a future block added before siteNav can never repeat this.
  const showPersistentChrome =
    state.phase !== 'intro' &&            // intro renders blocks itself
    state.phase !== 'resuming' &&
    state.phase !== 'external-redirect';
  const introBlocks = (a.config.intro.blocks as ContentBlock[] | undefined) ?? [];
  const rawNavBlock = introBlocks.find((b) => b.type === 'siteNav');
  const rawFooterBlock = introBlocks.find((b) => b.type === 'eventFooter');
  const withEventVars = (b: ContentBlock): ContentBlock => {
    const ec = (a.config as any).eventConfig ?? {};
    const knownKeys = ['eventDayLong', 'eventTimeShort', 'eventTimeRange', 'eventDateTimeFull',
      'eventDuration', 'eventDateOrdinal', 'eventWeekday', 'eventDayPart', 'eventDayRelative', 'eventDayRelativeLower',
      'eventUrl', 'bookingUrl', 'replayUrl',
      'replayClosesShort', 'replayClosesDay', 'replayClosesDate',
      'replayClosesOrdinal', 'replayClosesRelative', 'replayClosesDateTimeFull'];
    const eventVars: Record<string, string> = {};
    for (const k of knownKeys) {
      if (typeof ec[k] === 'string' && ec[k]) eventVars[k] = ec[k];
    }
    return Object.keys(eventVars).length ? interpolateBlocks([b], eventVars)[0] : b;
  };
  // ★ The gate phase serves three different states off ONE phase key, but
  // siteNav's ctaByPhase is keyed by phase alone — so `ctaByPhase.gate`
  // ("Complete your prep survey" → "#gate-cta") also rendered for the two
  // TERMINAL states, where GatePage early-returns before `#gate-cta` is ever
  // in the DOM. The result was a nav button that told someone to complete a
  // survey the same page had just told them they'd finished, and that did
  // nothing at all when clicked (dead anchor). Drop the CTA in those states:
  // the page itself says "nothing else for you to do right now", so the
  // honest nav is one without a call to action.
  const gateIsTerminal =
    state.phase === 'gate' &&
    (state.registrationStatus === 'registered-survey-complete' ||
      state.registrationStatus === 'registered-survey-opted-out');
  const stripNavCta = (b: ContentBlock): ContentBlock => {
    const nav = b as ContentBlock & { cta?: unknown; ctaByPhase?: Record<string, unknown> };
    const { cta: _cta, ctaByPhase: _ctaByPhase, ...rest } = nav;
    return rest as ContentBlock;
  };
  // The upgrade step's only actions are its own yes/no buttons. With no
  // ctaByPhase.upgrade entry the nav would fall back to its landing-page
  // "Register Now" — to someone whose seat is already saved.
  const navCtaHidden = gateIsTerminal || state.phase === 'upgrade';
  const persistentNavBlock = showPersistentChrome && rawNavBlock
    ? (navCtaHidden ? stripNavCta(withEventVars(rawNavBlock)) : withEventVars(rawNavBlock))
    : null;
  const persistentFooterBlock = showPersistentChrome && rawFooterBlock ? withEventVars(rawFooterBlock) : null;
  // intro.pageTheme carries through the whole funnel, not just the landing
  // (Michael, 2026-09-17: "continue the theme throughout, every page should
  // have a similar design").
  //
  // ★ This is only safe because the funnel surfaces are now explicitly
  // themed. The Ignite theme inverts text to white for a dark canvas, and
  // these phases render GENERIC engine blocks (callout, card, heading,
  // paragraph, list, stats) that were built for a light surface. The first
  // attempt at this shipped nothing but the class and produced dark-navy
  // text on a dark background — the "You're registered" banner, the "your
  // join link is on the way" line and the "What you'll walk away with" card
  // all invisible, on the screen every registrant sees immediately after
  // registering. If you add a new funnel surface, theme it in
  // blocks-events.css under `.tsb-evt-theme-ignite` or it inherits
  // light-surface colours onto a dark page.
  const questionsEl = (
    <Questions
      sections={a.config.sections}
      onComplete={(answers) => dispatch({ type: 'answers-complete', answers })}
      skipQuestionIds={skipQuestionIds}
      transitionMessage={transitionMessage}
      surveyMode={a.config.surveyMode === true}
      assessmentType={a.type}
    />
  );
  const stepTwoActive = stepTwoApplies(a.config.gatePage, state.registrationStatus, state.preview.active);

  const pageThemeClass = a.config.intro.pageTheme ? ` tsb-evt-theme-${a.config.intro.pageTheme}` : '';

  return (
    <BrandProvider brand={a.brand ?? null}>
    <PhaseProvider phase={state.phase}>
    <div className={`app-shell ${isFullWidth ? 'full-width' : 'centered'}${pageThemeClass}`}>
      {showLogo && (
        <div className="brand-bar">
          {logoLinkUrl ? (
            <a href={logoLinkUrl} className="brand-bar-link" aria-label={a.brand?.name ?? 'Home'}>
              <img src={effectiveLogoUrl!} alt={a.brand?.name ?? ''} />
            </a>
          ) : (
            <img src={effectiveLogoUrl!} alt={a.brand?.name ?? ''} />
          )}
        </div>
      )}
      {showTitle && (
        <div className="brand-bar brand-bar--title">
          {logoLinkUrl ? (
            <a href={logoLinkUrl} className="brand-bar-link" aria-label={programTitle ?? 'Home'}>
              <h1 className="brand-title">{programTitle}</h1>
            </a>
          ) : (
            <h1 className="brand-title">{programTitle}</h1>
          )}
          {branding?.programSubtitle && (
            <div className="brand-subtitle">{branding.programSubtitle}</div>
          )}
        </div>
      )}

      {/* Persistent header — see the showPersistentChrome/persistentNavBlock
          computation above the return statement. */}
      {persistentNavBlock && <Block block={persistentNavBlock} />}

      {/* Intermediate phase while verifying a resume token — shows a quiet loading state
          so we never flash the intro for widget-redirected users. */}
      {state.phase === 'resuming' && (
        <div className="loading-screen" style={{ minHeight: '50vh' }}>
          <div className="loading-spinner" />
          <span>Continuing your registration…</span>
        </div>
      )}

      {/* For hostingMode='external' with no active session: don't render the native intro.
          Point the user back at the external landing (HubSpot page) instead. */}
      {state.phase === 'external-redirect' && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 32px', maxWidth: 560 }}>
          <h1 style={{ marginTop: 0 }}>Registration lives on our landing page</h1>
          <p className="muted" style={{ margin: '12px 0 24px' }}>
            This event registers through our main site. Head there to save your seat.
          </p>
          {(a.config as any).externalLandingUrl ? (
            <a className="btn btn-primary" href={(a.config as any).externalLandingUrl}>Open the event page →</a>
          ) : (
            <p className="muted" style={{ fontSize: 13 }}>
              If you got here by mistake, please check your invitation email for the registration link.
            </p>
          )}
        </div>
      )}

      {state.phase === 'intro' && (
        <Intro
          config={a.config.intro}
          surveyMode={a.config.surveyMode === true}
          heroImageUrl={a.config.intro.pageStyle?.heroImageUrl ?? branding?.heroImageUrl}
          onStart={() => dispatch({ type: 'start' })}
          sampleSubmissionId={(a.config as any).sampleSubmissionId}
          hasSampleReport={!!(a.config as any).sampleReport}
          slug={a.slug}
          assessmentType={a.type}
          eventConfig={(a.config as any).eventConfig}
          eventPhase={(a.config as any).eventConfig?.phase}
        />
      )}

      {state.phase === 'email-gate' && a.config.emailGate && (
        <EmailGate
          config={a.config.emailGate}
          newsletterSlug={offersNewsletterOnEvent(a.slug) ? a.slug : null}
          onSubmit={(lead) => dispatch({ type: 'lead-captured', lead })}
        />
      )}

      {state.phase === 'upgrade' && a.config.gatePage?.upgradeStep && (
        <UpgradeStep
          gatePage={a.config.gatePage}
          leadEmail={state.preview.active ? null : state.lead?.email ?? null}
          leadFirstName={state.lead?.firstName ?? null}
          eventConfig={(a.config as any).eventConfig}
          onDecline={() => {
            let store: Storage | null = null;
            try { store = window.sessionStorage; } catch { /* blocked */ }
            if (!state.preview.active) writeDeclined(store, state.resumedSubmissionId);
            dispatch({ type: 'decline-upgrade' });
          }}
        />
      )}

      {state.phase === 'gate' && a.config.gatePage && (
        <GatePage
          config={a.config.gatePage}
          onContinue={() => {
            const prior = (window as any).__priorResult as PriorResult | undefined;
            dispatch({ type: 'gate-continue', priorResult: prior ?? { found: false } });
          }}
          registrationStatus={state.registrationStatus ?? null}
          surveyConfig={a.config.survey}
          resumedSubmissionId={state.resumedSubmissionId}
          onSurveyOptOut={(status) => dispatch({ type: 'survey-opted-out', status })}
          leadFirstName={state.lead?.firstName ?? null}
          eventConfig={(a.config as any).eventConfig}
          memberVip={state.memberVip ?? false}
          tierKey={state.tierKey ?? null}
          tierSource={state.tierSource ?? null}
        />
      )}

      {state.phase === 'questions' && (stepTwoActive ? (
        <StepTwoFrame
          gatePage={a.config.gatePage!}
          surveyConfig={a.config.survey}
          resumedSubmissionId={state.resumedSubmissionId}
          registrationStatus={state.registrationStatus ?? null}
          leadFirstName={state.lead?.firstName ?? null}
          eventConfig={(a.config as any).eventConfig}
          onSurveyOptOut={(status) => dispatch({ type: 'survey-opted-out', status })}
        >
          {questionsEl}
        </StepTwoFrame>
      ) : questionsEl)}

      {state.phase === 'submitting' && (
        <div className="submitting-screen">
          <div className="loading-spinner" />
          <h2>{(a.config as any).submittingHeadline ?? 'Building your results…'}</h2>
          <p>{(a.config as any).submittingSubhead ?? 'Scoring your answers'}</p>
        </div>
      )}

      {state.phase === 'result' && state.scores && (
        <Result
          assessment={a}
          scores={state.scores}
          submissionId={state.submissionId}
          answers={state.answers}
          lead={state.lead}
        />
      )}

      {/* Persistent footer — mirrors persistentNavBlock, rendered at the end
          of the phase content so it stays at the bottom of every non-intro
          screen instead of only appearing on the landing page. */}
      {persistentFooterBlock && <Block block={persistentFooterBlock} />}

      {state.preview.active && (
        <PreviewNav
          assessment={a}
          currentPhase={state.phase}
          registrationStatus={state.registrationStatus ?? null}
          tierKey={state.tierKey ?? null}
          dispatch={dispatch}
          onJump={(phase, opts) => dispatch({ type: 'preview-jump', phase, questionIdx: opts?.questionIdx, tierKey: opts?.tierKey })}
          onExit={() => {
            // Clear ?preview= from URL + reload without it.
            const p = new URLSearchParams(window.location.search);
            p.delete('preview'); p.delete('q'); p.delete('tier');
            const qs = p.toString();
            window.location.href = window.location.pathname + (qs ? `?${qs}` : '');
          }}
        />
      )}
    </div>
    </PhaseProvider>
    </BrandProvider>
  );
}

// ---------- Preview floating nav ----------
// In-flow stepper bar. Shows only the phases a taker can reach in sequence.
// The admin Settings → Preview tab (admin/src/App.tsx PreviewPagesSection) is the
// canonical full directory and also includes admin-only rows (phase variants, email
// previews, error screen). Keep these two surfaces intentionally distinct.
function PreviewNav({ assessment, currentPhase, registrationStatus, tierKey, onJump, onExit, dispatch }: {
  assessment: Assessment;
  currentPhase: Phase;
  registrationStatus: import('./types').RegistrationStatus | null;
  tierKey: string | null;
  onJump: (phase: Phase, opts?: { questionIdx?: number; tierKey?: string }) => void;
  onExit: () => void;
  dispatch: (action: any) => void;
}) {
  const cfg = assessment.config as any;
  const hostingMode = cfg.hostingMode ?? 'engine';
  const hasEmailGate = !!assessment.config.emailGate?.enabled;
  const hasGatePage = !!assessment.config.gatePage;
  const hasUpgradeStep = !!assessment.config.gatePage?.upgradeStep;
  const hasVipTierLine = !!assessment.config.gatePage?.tierLines?.vip;
  const isRegistration = (assessment as any).type === 'registration';
  const sections = assessment.config.sections ?? [];
  const questions = sections.flatMap((s: any) => (s.questions ?? []).map((q: any) => ({ ...q, sectionLabel: s.label })));
  const resultKeys = Object.keys(assessment.config.results ?? {});

  const [widgetPreviewOpen, setWidgetPreviewOpen] = React.useState(false);

  const phaseBtn = (
    label: string,
    phase: Phase,
    opts?: { questionIdx?: number; tierKey?: string },
    extra?: () => void,
    activeOverride?: boolean,
  ) => {
    const isActive = activeOverride !== undefined ? activeOverride : (currentPhase === phase && !opts);
    return (
      <button
        key={label + (opts?.questionIdx ?? '') + (opts?.tierKey ?? '')}
        onClick={() => { if (extra) extra(); onJump(phase, opts); }}
        style={{
          fontSize: 11,
          padding: '4px 9px',
          border: `1px solid ${isActive ? 'var(--color-accent, #FF6B35)' : 'rgba(0,0,0,0.15)'}`,
          background: isActive ? 'var(--color-accent, #FF6B35)' : 'rgba(255,255,255,0.9)',
          color: isActive ? '#fff' : '#1a1a2e',
          borderRadius: 6,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          fontWeight: 500,
        }}
      >
        {label}
      </button>
    );
  };

  const apiBase = (import.meta as any).env?.VITE_API_BASE ?? '';

  return (
    <>
      {/* Widget step-2 overlay — loads real embed bundle with mocked register response */}
      {widgetPreviewOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setWidgetPreviewOpen(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.25)', position: 'relative',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: '#FF6B35',
              textTransform: 'uppercase', marginBottom: 16,
            }}>Preview — Widget · Last name step</div>
            <WidgetStep2Preview
              accentColor={cfg.accentColor ?? '#ef6c35'}
              apiBase={apiBase}
              slug={(assessment as any).slug ?? ''}
            />
            <button
              onClick={() => setWidgetPreviewOpen(false)}
              style={{
                position: 'absolute', top: 12, right: 12, background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 18, color: '#9ca3af', lineHeight: 1,
              }}
            >✕</button>
          </div>
        </div>
      )}

      <div style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        background: 'rgba(255,255,255,0.98)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        maxWidth: 'calc(100vw - 32px)',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: '#FF6B35', textTransform: 'uppercase' }}>Preview</span>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          {hostingMode === 'external' && phaseBtn('External redirect', 'external-redirect')}
          {hostingMode !== 'external' && phaseBtn('Intro', 'intro')}
          {hasEmailGate && phaseBtn('Email gate', 'email-gate')}
          {/* Widget step-2 — shown for all registration types since the widget can
              be embedded on any landing regardless of engine/external hostingMode */}
          {isRegistration && (
            <button
              onClick={() => setWidgetPreviewOpen(true)}
              style={{
                fontSize: 11, padding: '4px 9px',
                border: '1px solid rgba(0,0,0,0.15)',
                background: 'rgba(255,255,255,0.9)', color: '#1a1a2e',
                borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 500,
              }}
            >Widget · last name</button>
          )}
          {hasUpgradeStep && phaseBtn('Upgrade step', 'upgrade')}
          {hasGatePage && phaseBtn(
            'Gate page', 'gate', undefined,
            () => dispatch({ type: 'set-registration-status', status: null }),
            currentPhase === 'gate' && !registrationStatus && !tierKey,
          )}
          {hasVipTierLine && phaseBtn(
            'Gate · VIP buyer', 'gate', { tierKey: 'vip' },
            () => dispatch({ type: 'set-registration-status', status: null }),
            currentPhase === 'gate' && !registrationStatus && tierKey === 'vip',
          )}
          {/* Gate terminal states — disambiguated by registrationStatus so only one highlights. */}
          {isRegistration && hasGatePage && (
            <>
              {phaseBtn('Gate · done', 'gate', undefined,
                () => dispatch({ type: 'set-registration-status', status: 'registered-survey-complete' }),
                currentPhase === 'gate' && registrationStatus === 'registered-survey-complete',
              )}
              {phaseBtn('Gate · skipped', 'gate', undefined,
                () => dispatch({ type: 'set-registration-status', status: 'registered-survey-opted-out' }),
                currentPhase === 'gate' && registrationStatus === 'registered-survey-opted-out',
              )}
            </>
          )}
          {questions.length > 0 && (
            <div style={{ display: 'flex', gap: 2 }}>
              {questions.map((_q: any, i: number) => phaseBtn(`Q${i + 1}`, 'questions', { questionIdx: i }))}
            </div>
          )}
          {phaseBtn('Submitting', 'submitting')}
          {resultKeys.length > 0 && (
            <div style={{ display: 'flex', gap: 2 }}>
              {resultKeys.map((k) => phaseBtn(k, 'result', { tierKey: k }))}
            </div>
          )}
        </div>
        <button
          onClick={onExit}
          style={{
            fontSize: 11,
            padding: '4px 10px',
            border: '1px solid rgba(0,0,0,0.15)',
            background: 'transparent',
            color: '#6b7280',
            borderRadius: 6,
            cursor: 'pointer',
            marginLeft: 4,
          }}
        >
          Exit preview
        </button>
      </div>
    </>
  );
}

// ---------- Widget step-2 preview component ----------
// Renders a faithful replica of the embed widget's last-name step inside the admin
// preview overlay. Uses the same CSS class names as the real widget so styling matches.
function WidgetStep2Preview({ accentColor, apiBase: _apiBase, slug: _slug }: {
  accentColor: string;
  apiBase: string;
  slug: string;
}) {
  const [lastName, setLastName] = React.useState('');
  const [error, setError] = React.useState('');

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16, color: '#111827', lineHeight: 1.35 }}>
        One more thing — your last name?
      </div>
      <input
        type="text"
        placeholder="Last name"
        value={lastName}
        onChange={e => { setLastName(e.target.value); setError(''); }}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 14px', fontSize: 15,
          border: `1.5px solid ${error ? '#ef4444' : '#d1d5db'}`, borderRadius: 8,
          outline: 'none', marginBottom: 4,
        }}
        autoFocus
      />
      {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        We use this to set up your community account.
      </div>
      <button
        style={{
          width: '100%', padding: '12px 0', fontSize: 15, fontWeight: 600,
          background: accentColor, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
        }}
        onClick={() => {
          if (!lastName.trim()) { setError('Please enter your last name.'); return; }
          setError('');
          alert('(Preview only — no real submission)');
        }}
      >
        Continue →
      </button>
      <div style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: '#9ca3af' }}>
        (Preview only — clicking Continue does not register)
      </div>
    </div>
  );
}
